import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth, anonymize } from "../App";
import { GakEak } from "../utils/caseFormat";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Truck, Calendar, Bell, ClipboardCheck, Send, Link2, CheckSquare, ArrowRight, Package, FolderOpen } from "lucide-react";
import { DateInput } from "../components/ui/date-input";

const STAGES = [
  { key: "prokatathesi", label: "Προκατάθεση", color: "bg-amber-100 text-amber-800" },
  { key: "prosdiorismos", label: "Προσδιορισμός", color: "bg-blue-100 text-blue-800" },
  { key: "prodikastikos", label: "Προδικαστικός Έλεγχος", color: "bg-indigo-100 text-indigo-800" },
  { key: "diakinisi", label: "Διακίνηση", color: "bg-purple-100 text-purple-800" },
  { key: "edra", label: "Έδρα", color: "bg-emerald-100 text-emerald-800" },
];

const Dispatch = () => {
  const { user } = useAuth();
  const privacy = user?.privacy_mode || false;
  const p = (text) => privacy ? anonymize(text) : text;
  const [incoming, setIncoming] = useState([]);
  const [upcomingHearings, setUpcomingHearings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [preHearing, setPreHearing] = useState([]);
  const [daysBefore, setDaysBefore] = useState(9);
  const [dispatchDate, setDispatchDate] = useState(new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeWorkflow, setActiveWorkflow] = useState("incoming");
  const [workflowCases, setWorkflowCases] = useState([]);
  const [workflowTotal, setWorkflowTotal] = useState(0);
  const [workflowStage, setWorkflowStage] = useState("all");
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [inc, hear, notif, pre] = await Promise.all([
        axios.get(`${API}/dispatch/incoming`, { headers }),
        axios.get(`${API}/dispatch/upcoming-hearings?limit=10`, { headers }),
        axios.get(`${API}/dispatch/notifications`, { headers }),
        axios.get(`${API}/dispatch/pre-hearing?days_before=${daysBefore}`, { headers })
      ]);
      setIncoming(inc.data);
      setUpcomingHearings(hear.data);
      setNotifications(notif.data);
      setPreHearing(pre.data);
    } catch { toast.error("Σφάλμα φόρτωσης"); }
    finally { setLoading(false); }
  };

  const fetchWorkflow = async (stage) => {
    try {
      const res = await axios.get(`${API}/dispatch/by-stage?stage=${stage}&page=1&page_size=100`, { headers });
      setWorkflowCases(res.data.cases);
      setWorkflowTotal(res.data.total);
    } catch { toast.error("Σφάλμα φόρτωσης workflow"); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, [daysBefore]);

  useEffect(() => {
    if (activeWorkflow === "workflow") fetchWorkflow(workflowStage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkflow, workflowStage]);

  const handleDispatchToBench = async (caseIds) => {
    try {
      await axios.post(`${API}/dispatch/send-to-bench`, { case_ids: caseIds, dispatch_date: dispatchDate }, { headers });
      toast.success("Διακίνηση προς Έδρα ολοκληρώθηκε");
      fetchAll(); setSelectedIds([]);
    } catch { toast.error("Σφάλμα διακίνησης"); }
  };

  const handleMoveStage = async (caseIds, stage) => {
    try {
      const res = await axios.post(`${API}/dispatch/move-stage`, { case_ids: caseIds, stage }, { headers });
      toast.success(res.data.message);
      fetchAll(); fetchWorkflow(workflowStage); setSelectedIds([]);
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα μεταφοράς"); }
  };

  const handleAssociate = async (caseId) => {
    try {
      await axios.post(`${API}/dispatch/associate`, { case_id: caseId }, { headers });
      toast.success("Συσχέτιση ολοκληρώθηκε"); fetchAll();
    } catch { toast.error("Σφάλμα συσχέτισης"); }
  };

  const handleReceive = async (caseId) => {
    try {
      await axios.post(`${API}/dispatch/receive`, { case_id: caseId, receive_date: dispatchDate }, { headers });
      toast.success("Παραλαβή καταχωρήθηκε"); fetchAll();
    } catch { toast.error("Σφάλμα παραλαβής"); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = (cases) => {
    const ids = cases.map(c => c.id);
    setSelectedIds(prev => ids.every(id => prev.includes(id)) ? prev.filter(x => !ids.includes(x)) : [...new Set([...prev, ...ids])]);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("el-GR") : "-";

  return (
    <div className="space-y-6" data-testid="dispatch-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Διακίνηση</h1>
        <div className="flex items-center gap-3">
          <DateInput value={dispatchDate} onChange={e => setDispatchDate(e.target.value)}
            className="w-48" data-testid="dispatch-date" />
        </div>
      </div>

      {/* Workflow Stage Indicator */}
      <div className="flex items-center gap-1 overflow-x-auto pb-2">
        {STAGES.map((s, i) => (
          <div key={s.key} className="flex items-center">
            <button
              onClick={() => { setActiveWorkflow("workflow"); setWorkflowStage(s.key); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                workflowStage === s.key && activeWorkflow === "workflow"
                  ? s.color + " ring-2 ring-offset-1 ring-slate-400"
                  : "bg-slate-100 text-slate-500 hover:bg-slate-200"
              }`}
              data-testid={`stage-${s.key}`}
            >
              {s.label}
            </button>
            {i < STAGES.length - 1 && <ArrowRight className="w-3 h-3 text-slate-300 mx-1" />}
          </div>
        ))}
      </div>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <Card className="border-blue-300 bg-blue-50/50">
          <CardContent className="p-3 flex items-center justify-between">
            <span className="text-sm font-medium text-blue-800">
              <CheckSquare className="w-4 h-4 inline mr-1" />
              {selectedIds.length} υποθέσεις επιλεγμένες
            </span>
            <div className="flex gap-2">
              {STAGES.map(s => (
                <Button key={s.key} size="sm" variant="outline" className="text-xs"
                  onClick={() => handleMoveStage(selectedIds, s.key)}
                  data-testid={`bulk-move-${s.key}`}>
                  <ArrowRight className="w-3 h-3 mr-1" /> {s.label}
                </Button>
              ))}
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}>Ακύρωση</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeWorkflow} onValueChange={v => { setActiveWorkflow(v); setSelectedIds([]); }} className="w-full">
        <TabsList className="grid w-full grid-cols-5 bg-slate-100 h-11">
          <TabsTrigger value="incoming" data-testid="tab-incoming">
            <Truck className="w-4 h-4 mr-1" /> Εισερχόμενες
          </TabsTrigger>
          <TabsTrigger value="workflow" data-testid="tab-workflow">
            <ArrowRight className="w-4 h-4 mr-1" /> Workflow
          </TabsTrigger>
          <TabsTrigger value="hearings" data-testid="tab-hearings">
            <Calendar className="w-4 h-4 mr-1" /> Δικάσιμοι
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">
            <Bell className="w-4 h-4 mr-1" /> Ειδοποιήσεις
          </TabsTrigger>
          <TabsTrigger value="prehearing" data-testid="tab-prehearing">
            <ClipboardCheck className="w-4 h-4 mr-1" /> Έλεγχος
          </TabsTrigger>
        </TabsList>

        {/* Εισερχόμενες */}
        <TabsContent value="incoming">
          <Card>
            <CardHeader>
              <CardTitle>Εισερχόμενες Υποθέσεις</CardTitle>
              <CardDescription>Ολοκληρωμένος προδικαστικός - προς παραλαβή</CardDescription>
            </CardHeader>
            <CardContent>
              {incoming.length === 0 ? <p className="text-slate-500 text-sm">Δεν υπάρχουν εισερχόμενες</p> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <input type="checkbox" onChange={() => toggleSelectAll(incoming)}
                          checked={incoming.length > 0 && incoming.every(c => selectedIds.includes(c.id))} />
                      </TableHead>
                      <TableHead>ΓΑΚ/ΕΑΚ</TableHead>
                      <TableHead>Τύπος</TableHead>
                      <TableHead>Δικάσιμος</TableHead>
                      <TableHead>Φάκ./BOX</TableHead>
                      <TableHead>Κατάσταση</TableHead>
                      <TableHead>Ενέργειες</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {incoming.map(c => {
                      const filings = c.party_filings || [];
                      const totalFolders = filings.reduce((s,f) => s + (f.folders_count||0), 0);
                      const totalBox = filings.reduce((s,f) => s + (f.box_count||0), 0);
                      return (
                      <TableRow key={c.id}>
                        <TableCell>
                          <input type="checkbox" checked={selectedIds.includes(c.id)}
                            onChange={() => toggleSelect(c.id)} />
                        </TableCell>
                        <TableCell className="font-mono font-semibold">
                          <Link to={`/cases/${c.id}`} className="hover:text-blue-600">
                            <GakEak caseObj={c} />
                          </Link>
                          {c.eak_number && <p className="text-xs text-indigo-600">ΕΑΚ {c.eak_number}</p>}
                        </TableCell>
                        <TableCell>{c.court_type}</TableCell>
                        <TableCell>{formatDate(c.hearing_date)}</TableCell>
                        <TableCell>
                          {totalFolders > 0 || totalBox > 0 ? (
                            <span className="flex items-center gap-1 text-sm">
                              <FolderOpen className="w-3 h-3" />{totalFolders}
                              <Package className="w-3 h-3 ml-1" />{totalBox}
                            </span>
                          ) : "-"}
                        </TableCell>
                        <TableCell>
                          <Badge className={c.dispatch_status === "dispatched" ? "bg-green-600 text-white" : "bg-amber-500 text-white"}>
                            {c.dispatch_status === "dispatched" ? "Διακινήθηκε" : "Προς Παραλαβή"}
                          </Badge>
                        </TableCell>
                        <TableCell className="space-x-1">
                          <Button size="sm" variant="outline" onClick={() => handleReceive(c.id)}>
                            <ClipboardCheck className="w-3 h-3 mr-1" /> Παραλαβή
                          </Button>
                          <Button size="sm" onClick={() => handleDispatchToBench([c.id])}>
                            <Send className="w-3 h-3 mr-1" /> Έδρα
                          </Button>
                        </TableCell>
                      </TableRow>
                    );})}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Workflow by Stage */}
        <TabsContent value="workflow">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Ροή Διακίνησης ({workflowTotal})</CardTitle>
                  <CardDescription>
                    {STAGES.find(s => s.key === workflowStage)?.label || "Όλα τα στάδια"}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {workflowCases.length === 0 ? (
                <p className="text-slate-500 text-sm">Δεν υπάρχουν υποθέσεις σε αυτό το στάδιο</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <input type="checkbox" onChange={() => toggleSelectAll(workflowCases)}
                          checked={workflowCases.length > 0 && workflowCases.every(c => selectedIds.includes(c.id))} />
                      </TableHead>
                      <TableHead>ΓΑΚ/ΕΑΚ</TableHead>
                      <TableHead>Τύπος</TableHead>
                      <TableHead>Κατάθεση</TableHead>
                      <TableHead>Δικάσιμος</TableHead>
                      <TableHead>Δικαστής</TableHead>
                      <TableHead>Φάκ./BOX</TableHead>
                      <TableHead>Στάδιο</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {workflowCases.map(c => {
                      const stageInfo = STAGES.find(s => s.key === c.dispatch_status);
                      return (
                        <TableRow key={c.id}>
                          <TableCell>
                            <input type="checkbox" checked={selectedIds.includes(c.id)}
                              onChange={() => toggleSelect(c.id)} />
                          </TableCell>
                          <TableCell className="font-mono font-semibold">
                            <Link to={`/cases/${c.id}`} className="hover:text-blue-600">
                              <GakEak caseObj={c} />
                            </Link>
                            {c.eak_number && <p className="text-xs text-indigo-600">ΕΑΚ {c.eak_number}</p>}
                          </TableCell>
                          <TableCell>{c.court_type}</TableCell>
                          <TableCell className="text-sm">{c.filing_date || "-"}</TableCell>
                          <TableCell className="text-sm">{c.hearing_date || c.last_hearing_date || "-"}</TableCell>
                          <TableCell className="text-sm">{c.assigned_judge || "-"}</TableCell>
                          <TableCell>
                            {(c.folders_count > 0 || c.box_count > 0) ? (
                              <span className="text-sm">{c.folders_count}Φ / {c.box_count}B</span>
                            ) : "-"}
                          </TableCell>
                          <TableCell>
                            {stageInfo ? (
                              <Badge className={stageInfo.color}>{stageInfo.label}</Badge>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-600">{c.dispatch_status || "-"}</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Δικάσιμοι */}
        <TabsContent value="hearings">
          <Card>
            <CardHeader><CardTitle>Επερχόμενες Δικάσιμοι</CardTitle></CardHeader>
            <CardContent>
              {upcomingHearings.map((h, i) => (
                <div key={i} className="flex items-center justify-between p-3 border-b last:border-0">
                  <div>
                    <p className="font-semibold">{formatDate(h.hearing_date)}</p>
                    <p className="text-sm text-slate-500">{h.court_type}</p>
                  </div>
                  <Badge>{h.count} υποθέσεις</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ειδοποιήσεις */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader><CardTitle>Πινάκια Επόμενου Δεκαπενθημέρου</CardTitle></CardHeader>
            <CardContent>
              {notifications.length === 0 ? <p className="text-slate-500 text-sm">Δεν υπάρχουν</p> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ΓΑΚ</TableHead><TableHead>Τύπος</TableHead><TableHead>Δικάσιμος</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notifications.map(c => (
                      <TableRow key={c.id}>
                        <TableCell className="font-mono">ΓΑΚ {c.gak_number}/{c.gak_year} · ΕΑΚ {c.eak_number || "—"}/{c.gak_year}</TableCell>
                        <TableCell>{c.court_type}</TableCell>
                        <TableCell className="font-semibold text-indigo-700">{formatDate(c.hearing_date)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Έλεγχος */}
        <TabsContent value="prehearing">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Έλεγχος Παραλαβής</CardTitle>
                  <CardDescription>Υποθέσεις εντός {daysBefore} ημερών πριν δικάσιμο</CardDescription>
                </div>
                <Input type="number" min="1" value={daysBefore} onChange={e => setDaysBefore(parseInt(e.target.value) || 9)}
                  className="w-20" data-testid="days-before-input" />
              </div>
            </CardHeader>
            <CardContent>
              {preHearing.length === 0 ? <p className="text-slate-500 text-sm">Δεν υπάρχουν</p> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <input type="checkbox" onChange={() => toggleSelectAll(preHearing)}
                          checked={preHearing.every(c => selectedIds.includes(c.id))} />
                      </TableHead>
                      <TableHead>ΓΑΚ/ΕΑΚ</TableHead><TableHead>Τύπος</TableHead>
                      <TableHead>Δικάσιμος</TableHead><TableHead>Ενέργειες</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preHearing.map(c => (
                      <TableRow key={c.id}>
                        <TableCell>
                          <input type="checkbox" checked={selectedIds.includes(c.id)} onChange={() => toggleSelect(c.id)} />
                        </TableCell>
                        <TableCell className="font-mono font-semibold">
                          ΓΑΚ {c.gak_number}/{c.gak_year} · ΕΑΚ {c.eak_number || "—"}/{c.gak_year}
                        </TableCell>
                        <TableCell>{c.court_type}</TableCell>
                        <TableCell>{formatDate(c.hearing_date)}</TableCell>
                        <TableCell className="space-x-1">
                          <Button size="sm" variant="outline" onClick={() => handleReceive(c.id)}>Παραλαβή</Button>
                          <Button size="sm" onClick={() => handleDispatchToBench([c.id])}>Έδρα</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dispatch;
